'use client'

import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { PreferencesDialog } from './preferences-dialog'
import { CookiePolicyPageData } from '@/lib/api/strapi/browser-cookies/cookies.types'
import RichText from '../rich-text'
import {
  CookiePreferences,
  getBrowserCookie,
  setBrowserCookie,
} from '@/lib/utils/browser-cookies'
import { Dictionary } from '@/localization/dictionary'

type CookiePolicyBannerProps = {
  className?: string
  data: CookiePolicyPageData
  dict: Dictionary['cookieBanner']
}

export function CookiePolicyBanner({ data, dict }: CookiePolicyBannerProps) {
  const [open, setOpen] = useState<boolean>(false)

  useEffect(() => {
    // Check if the user has already accepted or rejected cookies
    const cookieConsent = getBrowserCookie('cookie-consent')
    if (cookieConsent !== 'true') {
      // Small delay to ensure the sheet appears after page load
      const timer = setTimeout(() => setOpen(true), 500)
      return () => clearTimeout(timer)
    }
  }, [])

  const handleAccept = () => {
    // Accept all cookies by setting all preferences to true
    const allPreferences: CookiePreferences = {
      essential: true,
      marketing: true,
      externalMedia: true,
      statistic: true,
    }
    setBrowserCookie('cookie-consent', 'true')
    setBrowserCookie('cookie-preferences', JSON.stringify(allPreferences))
    setOpen(false)
  }

  const handleEssentialOnly = () => {
    // Only allow essential cookies
    const essentialOnly: CookiePreferences = {
      essential: true,
      marketing: false,
      externalMedia: false,
      statistic: false,
    }
    setBrowserCookie('cookie-consent', 'true')
    setBrowserCookie('cookie-preferences', JSON.stringify(essentialOnly))
    setOpen(false)
  }

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="bottom" className="px-6 py-4 md:px-10">
        <div className="container max-w-nav">
          <SheetHeader className="mb-10">
            <SheetTitle className="text-2xl font-normal uppercase">
              {data.your_privacy.title}
            </SheetTitle>
            <SheetDescription asChild>
              <div className="text-sm">
                <RichText content={data.your_privacy.content} />
              </div>
            </SheetDescription>
          </SheetHeader>
          <div className="flex flex-col space-y-2 sm:flex-row sm:space-x-[3.4375rem] sm:space-y-0">
            <PreferencesDialog
              data={data.privacy_policy}
              onAccept={() => setOpen(false)}
              onDecline={handleEssentialOnly}
              dict={dict}
            />
            <Button onClick={handleEssentialOnly} variant="outline">
              {dict.rejectNonEssential}
            </Button>
            <Button onClick={handleAccept}>{dict.acceptAll}</Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
